import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight, MapPin, Waves, Flame, Thermometer, Bath, Dumbbell,
  Trees, Flower2, Umbrella, Sparkles, type LucideIcon,
} from 'lucide-react';
import type { UnitFacility, FacilityType } from '@/types/database';
import { ScopeBadge } from './GuestUI';
import { useTranslatedFacility } from '@/hooks/useTranslatedContent';

// Lucide icon per facility type — keeps the guest portal on ONE icon language
// (no emoji), matching the rest of the app. Colour comes from the section group
// (emerald), not per-type, so the cards look uniform.
const FACILITY_ICONS: Record<FacilityType, LucideIcon> = {
  pool: Waves,
  bbq: Flame,
  sauna: Thermometer,
  jacuzzi: Bath,
  gym: Dumbbell,
  garden: Trees,
  terrace: Flower2,
  beach_access: Umbrella,
  other: Sparkles,
};

interface FacilitiesSectionProps {
  facilities: UnitFacility[];
  darkMode: boolean;
}

// Facility Card Component
function FacilityCard({
  facility,
  darkMode,
}: {
  facility: UnitFacility;
  darkMode: boolean;
}) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const FacilityIcon = FACILITY_ICONS[facility.facility_type] || Sparkles;

  // Translate the host's free-text into the guest's language. The hook's
  // `usage_instructions` field maps to the facility's `usage_rules` column.
  const { translations: tr } = useTranslatedFacility({
    id: facility.id,
    name: facility.name,
    description: facility.description,
    location_description: facility.location_description,
    usage_instructions: facility.usage_rules,
  });
  const tName = tr.name?.translated || facility.name;
  const tDescription = tr.description?.translated || facility.description;
  const tLocation = tr.location_description?.translated || facility.location_description;
  const tUsageRules = tr.usage_instructions?.translated || facility.usage_rules;

  // Only offer the expandable detail when there's actually something inside —
  // otherwise expanding shows an empty panel (e.g. a BBQ with just a name).
  const hasDetail = !!(
    facility.description ||
    facility.location_description ||
    facility.usage_rules ||
    facility.requires_booking ||
    facility.max_users
  );

  return (
    <div
      className={`rounded-2xl overflow-hidden border ${
        darkMode
          ? 'bg-slate-800/40 border-slate-700/50'
          : 'bg-white border-gray-200/70'
      }`}
    >
      <button
        onClick={() => hasDetail && setIsExpanded(!isExpanded)}
        aria-expanded={hasDetail ? isExpanded : undefined}
        className={`w-full p-4 flex items-center gap-3 transition-colors ${
          hasDetail ? (darkMode ? 'hover:bg-slate-700/30' : 'hover:bg-gray-50') : 'cursor-default'
        }`}
      >
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
            darkMode ? 'bg-emerald-500/20' : 'bg-emerald-50'
          }`}
        >
          <FacilityIcon className={`w-5 h-5 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
        </div>
        <div className="flex-1 text-left min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {tName}
            </h4>
            {/* Scope: a unit_facilities row is always THIS unit's. */}
            <ScopeBadge scope="unit" label={t('guest.scope.unitOnly', 'Samo ovaj smještaj')} darkMode={darkMode} />
          </div>
          {facility.opening_hours && (
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              {facility.opening_hours}
            </p>
          )}
        </div>
        {hasDetail && (
          <ChevronRight
            className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''} ${
              darkMode ? 'text-slate-400' : 'text-gray-400'
            }`}
          />
        )}
      </button>

      {hasDetail && isExpanded && (
        <div
          className={`px-4 pb-4 space-y-3 ${
            darkMode ? 'border-t border-slate-700/50' : 'border-t border-gray-100'
          }`}
        >
          {tDescription && (
            <p className={`text-sm pt-3 ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
              {tDescription}
            </p>
          )}

          {tLocation && (
            <div
              className={`flex items-center gap-2 text-sm ${
                darkMode ? 'text-slate-400' : 'text-gray-500'
              }`}
            >
              <MapPin className="w-4 h-4" />
              {tLocation}
            </div>
          )}

          {tUsageRules && (
            <div className={`p-3 rounded-xl ${darkMode ? 'bg-amber-500/10' : 'bg-amber-50'}`}>
              <p
                className={`text-sm font-medium mb-1 ${
                  darkMode ? 'text-amber-300' : 'text-amber-700'
                }`}
              >
                {t('guest.facilities.usageRules', 'Pravila korištenja')}
              </p>
              <p className={`text-sm ${darkMode ? 'text-amber-200' : 'text-amber-600'}`}>
                {tUsageRules}
              </p>
            </div>
          )}

          {facility.requires_booking && (
            <p className={`text-sm ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
              {t('guest.facilities.requiresBooking', 'Potrebna rezervacija')}
            </p>
          )}

          {facility.max_users && (
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              {t('guest.facilities.maxUsers', 'Maksimalno {{count}} osoba', {
                count: facility.max_users,
              })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// Main Section Component
export function FacilitiesSection({ facilities, darkMode }: FacilitiesSectionProps) {
  const { t } = useTranslation();

  if (!facilities || facilities.length === 0) {
    return null;
  }

  return (
    <div
      className={`rounded-3xl p-5 border ${
        darkMode
          ? 'bg-slate-800 border-slate-700 shadow-xl shadow-black/40 ring-1 ring-white/5'
          : 'bg-white border-gray-200/80 shadow-[0_1px_3px_rgba(0,0,0,0.05),0_12px_32px_-14px_rgba(15,23,42,0.22)]'
      }`}
    >
      <h3 className={`font-bold text-base mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        {t('guest.facilities.title', 'Sadržaji objekta')}
      </h3>
      <div className="space-y-3">
        {facilities.map((facility) => (
          <FacilityCard key={facility.id} facility={facility} darkMode={darkMode} />
        ))}
      </div>
    </div>
  );
}

export default FacilitiesSection;
